import React, { useState, useEffect, useRef } from "react";
import { ClaySelect, ClayInput, ClayButton } from "@clayui/form";
import PlantPerformanceTableCheckingDP from "./PlantPerformanceTableCheckingDP";
import { getProductAndForms } from "../../../../api/nebProductService";
import { toast } from "react-toastify";
import { faFilePdf, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { exportToPdf, exportToExcel } from "./exportToPdf";
import {
  Row,
  Col,
  Card,
  Form,
  InputGroup,
  Modal,
  Button,
  Table,
  Badge,
  Accordion,
} from "react-bootstrap";

const PlantPerformanceTableDP = ({
  tabCode,
  tabTitle,
  viewMode,
  fetchedReport,
  updateCheckedFormsCount,
  selectedQuarter,
  selectedYear,
  combinedStructure,
  setCombinedStructure,
  reportMap,
  setReportMap,
  reportStatus,
  isDisabled,
  onValidationChange,
}) => {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [productAndFormsList, setProductAndFormsList] = useState([]);
  const [template, setTemplate] = useState({});
  const [checkingReport, setCheckingReport] = useState();
  const [reportBasicInfo, setReportBasicInfo] = useState();
  const [fuelValues, setFuelValues] = useState([]);

  const inputStyle = {
    textAlign: "right",
    width: "100%",
    height: "30px",
    marginRight: "5px",
    borderRadius: "5px",
  };

  useEffect(() => {
    getProductAndForms("0", "POW")
      .then((result) => {
        console.log("FETCHING plant forms", JSON.stringify(result, null, 2));
        setProductAndFormsList(result);
      })
      .catch((error) => {
        console.error("Error fetching product and forms:", error);
        toast.error("Failed to fetch product and forms data.");
      });
  }, []);

  const normalizeForms = (forms) =>
    forms.map((form) => ({
      ...form,
      formFieldCode: form.formCode || form.formFieldCode,
      value: form.value ?? "",
    }));

  useEffect(() => {
    const isNewSubmission = viewMode === "new" || viewMode === undefined;

    if (!productAndFormsList.length) return;

    if (!fetchedReport?.forms && !isNewSubmission) return;

    setTemplate(productAndFormsList[0]);

    const powForm = fetchedReport?.forms?.find((f) => f.formCode === "POW");
    const powCategories = powForm?.categories || [];

    if (fuelValues.length === 0) {
      const baseTemplate = productAndFormsList[0]?.forms || [];

      if (!isNewSubmission && powCategories.length > 0) {
        const hydratedRows = powCategories.map((category) => {
          const forms = normalizeForms(
            baseTemplate.map((formTemplate) => {
              const matchingProduct = category.products.find(
                (product) => product.formCode === formTemplate.formFieldCode
              );

              return {
                ...formTemplate,
                value: matchingProduct ? matchingProduct.value : "",
              };
            })
          );

          return {
            index: category.index,
            plantName: category.plantName,
            plantType: category.plantType,
            fuel: category.fuel,
            balance: 0,
            forms,
          };
        });

        setFuelValues(hydratedRows);
      } else {
        // New submission: initialize blank row
        const defaultForms = baseTemplate.map((form) => ({
          ...form,
          value: "",
        }));

        setFuelValues([
          {
            index: 0,
            plantName: "",
            plantType: "",
            fuel: "",
            balance: 0,
            forms: defaultForms,
          },
        ]);
      }
    }
  }, [productAndFormsList, fetchedReport, viewMode]);

  useEffect(() => {
    const powCategories = [];

    fuelValues.forEach((fuelItem) => {
      let category = {
        index: fuelItem.index,
        plantName: fuelItem.plantName || "",
        plantType: fuelItem.plantType || "",
        fuel: fuelItem.fuel || "",
        products: [],
      };

      fuelItem.forms.forEach((form) => {
        category.products.push({
          formCode: form.formFieldCode,
          fieldName: form.formFieldName || "",
          value: Number(form.value || 0),
        });
      });

      powCategories.push(category);
    });

    setReportMap((prev) => ({
      ...prev,
      reportPeriodYear: selectedYear,
      reportQuarter: selectedQuarter,
      forms: [
        ...(prev.forms || []).filter((form) => form.formCode !== "POW"),
        {
          formCode: "POW",
          categories: powCategories,
        },
      ],
    }));
  }, [fuelValues, selectedQuarter, selectedYear]);

  useEffect(() => {
    const listErrors = {};
    let atLeastOneRowFilled = false;

    fuelValues.forEach((item, index) => {
      const isFormFieldFilled = item.forms?.some(
        (form) => Number(form.value || 0) !== 0
      );

      const isRowFilled =
        (item.plantName || "").trim() !== "" ||
        (item.plantType || "").trim() !== "" ||
        (item.fuel || "").trim() !== "" ||
        isFormFieldFilled;

      if (isRowFilled) {
        atLeastOneRowFilled = true;
        if (!isFormFieldFilled) {
          listErrors[
            `row_${index}`
          ] = `At least one percentage field must be filled in for row ${
            index + 1
          }`;
        }
      }
    });

    if (!atLeastOneRowFilled) {
      listErrors["row_required"] = "At least one row must have input.";
    }

    if (typeof onValidationChange === "function") {
      onValidationChange({ [tabTitle]: listErrors });
    }
  }, [fuelValues]);

  const handleAddNewPlant = () => {
    if (!productAndFormsList?.length) {
      toast.error("Cannot add plant: form data not loaded.");
      return;
    }

    const newIndex = fuelValues.length;
    const defaultForms = productAndFormsList?.[0]?.forms || [];
    const initialForms = defaultForms.map((form) => ({
      ...form,
      value: "",
      formFieldCode: form.formFieldCode,
      categories:
        form.categories?.map((cat) => ({
          ...cat,
          value: 0,
        })) || [],
    }));

    setFuelValues((prev) => [
      ...prev,
      {
        index: newIndex,
        plantName: "",
        plantType: "",
        fuel: "",
        balance: 0,
        forms: initialForms,
      },
    ]);
  };

  const handleDeleteRow = (indexToDelete) => {
    setFuelValues((prev) =>
      prev.filter((item) => item.index !== indexToDelete)
    );
  };

  const handleFieldChange = (index, field, value) => {
    setFuelValues((prev) =>
      prev.map((item) =>
        item.index === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleFuelValueChange = (e, fuelIndex, formCode, formName) => {
    const inputValue = e.target.value;

    // if empty, continue update
    if (inputValue === "") {
      updateFuelValue("", fuelIndex, formCode, formName);
      return;
    }

    const value = parseFloat(inputValue);
    if (isNaN(value)) return;

    if (value < 0) {
      toast.info("Percentage must be above 0");
      return;
    }

    if (value > 100) {
      toast.info("Percentage must be below 100");
      return;
    }

    updateFuelValue(value, fuelIndex, formCode, formName);
  };

  const updateFuelValue = (value, fuelIndex, formCode, formName) => {
    setFuelValues((prevFuelValues) =>
      prevFuelValues.map((fuel) => {
        if (fuel.index !== fuelIndex) return fuel;

        const updatedForms = fuel.forms.map((form) => {
          if (form.formFieldCode === formCode) {
            return {
              ...form,
              value,
            };
          }
          return form;
        });

        return {
          ...fuel,
          forms: updatedForms,
        };
      })
    );
  };

  return (
    <div>
      {!isDisabled && (
        <Button variant="success" className="mb-3" onClick={handleAddNewPlant}>
          Add New Plant Row
        </Button>
      )}

      <Accordion defaultActiveKey="0" alwaysOpen>
        {fuelValues &&
          fuelValues.map((fuel, idx) => (
            <Accordion.Item key={fuel.index} eventKey={idx.toString()}>
              <Accordion.Header>Power Plant {idx + 1}</Accordion.Header>
              <Accordion.Body>
                <Table responsive bordered hover size="sm">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Plant Name</td>
                      <td>
                        <ClayInput
                          value={fuel.plantName}
                          onChange={(e) =>
                            handleFieldChange(
                              fuel.index,
                              "plantName",
                              e.target.value
                            )
                          }
                          disabled={isDisabled}
                          placeholder="Plant Name"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Plant Type</td>
                      <td>
                        <ClaySelect
                          value={fuel.plantType}
                          onChange={(e) =>
                            handleFieldChange(
                              fuel.index,
                              "plantType",
                              e.target.value
                            )
                          }
                          disabled={isDisabled}
                        >
                          <option value="">Select Type</option>
                          <option value="CCGT">CCGT</option>
                          <option value="OCGT">OCGT</option>
                          <option value="Thermal">Thermal</option>
                          <option value="Conventional Thermal">
                            Conventional Thermal
                          </option>
                        </ClaySelect>
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Fuel Type</td>
                      <td>
                        <ClaySelect
                          value={fuel.fuel}
                          onChange={(e) =>
                            handleFieldChange(
                              fuel.index,
                              "fuel",
                              e.target.value
                            )
                          }
                          disabled={isDisabled}
                        >
                          <option value="">Select Fuel</option>
                          <option value="Hydro">Hydro</option>
                          <option value="Mini Hydro">Mini Hydro</option>
                          <option value="Gas">Gas</option>
                          <option value="Coal">Coal</option>
                          <option value="Diesel">Diesel</option>
                          <option value="MFO">MFO</option>
                        </ClaySelect>
                      </td>
                    </tr>
                    {Array.isArray(template?.forms) &&
                      template.forms.map((formTemplate) => (
                        <tr
                          key={`form_${fuel.index}_${formTemplate.formFieldCode}`}
                        >
                          <td>
                            <label
                              htmlFor={`input_${fuel.index}_${formTemplate.formFieldCode}`}
                            >
                              {formTemplate.formFieldName}
                            </label>
                          </td>
                          <td>
                            <ClayInput
                              id={`input_${fuel.index}_${formTemplate.formFieldCode}`}
                              style={inputStyle}
                              name={formTemplate.formFieldCode}
                              type="number"
                              min="0"
                              max="100"
                              disabled={isDisabled}
                              value={
                                fuel.forms.find(
                                  (f) =>
                                    f.formFieldCode ===
                                    formTemplate.formFieldCode
                                )?.value ?? ""
                              }
                              onChange={(e) =>
                                handleFuelValueChange(
                                  e,
                                  fuel.index,
                                  formTemplate.formFieldCode,
                                  formTemplate.formFieldName
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
                {!isDisabled && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteRow(fuel.index)}
                    className="mt-2"
                  >
                    Delete This Row
                  </Button>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
      </Accordion>
    </div>
  );
};

export default PlantPerformanceTableDP;
